import * as esprima from 'esprima';
import * as escodegen from 'escodegen';


class MLExpressionValidationException {
  constructor(pDescription) {
    this.description = pDescription;
    this.type = 'MLExpressionValidationException';
  }
}

/**
 * Splits an conditional expression into the left expression, operator and the right expression.
 * @param {*} pString Expression
 * @returns {Array} an array of the format [left expression, operator, right expression]
 */
export function getLeftOperatorRightFromExpression(pString) {
  let lConditionExpressionValues = [];

  try {
    const parsed = esprima.parseScript(pString);
    const expression = parsed?.body[0]?.expression;

    if (expression?.type != "BinaryExpression") {
      console.error('could not split expression (left|operator|right), set everything as left', pString);
      lConditionExpressionValues = [pString, '', ''];
    }
    else {
      const left = escodegen.generate(expression.left);
      const operator = expression.operator;
      const right = escodegen.generate(expression.right);
      lConditionExpressionValues = [left, operator, right];
    }
  }
  catch (e) {
    console.error('could not split expression (left|operator|right), set everything as left', pString);
    lConditionExpressionValues = [pString, '', ''];
  }

  return lConditionExpressionValues;
}


/**
 * Validates if the given string is a valid javascript expression
 * @param {object} pApplicationParametersObject
 * @returns {Function} a validation function wrt to the given application parameters
 */
export function getExpressionValidationFunction(pApplicationParametersObject) {

  function processDeclaration(pDeclaration, pParameters, pLambdas) {
    if (pDeclaration.type === 'ObjectExpression') {
      // Example: { x: 100, b: TOP, c: 'hi' }
      // process each value of the object
      pDeclaration.properties.forEach(property => {
        processDeclaration(property.value, pParameters, pLambdas);
      });
    }
    else if (pDeclaration.type === 'ArrayExpression') {
      // Example: [ 100, TOP, 'hi']
      // process each element of the array
      pDeclaration.elements.forEach(element => {
        processDeclaration(element, pParameters, pLambdas);
      });
    }
    else if (pDeclaration.type === 'CallExpression') {
      // Example: myFunction(100, TOP, 'hi) or [100, TOP, 'hi'].includes(100)
      // process arguments
      pDeclaration.arguments.forEach(argument => {
        processDeclaration(argument, pParameters, pLambdas);
      });
      // process callee if not a Identifier else add to list of lambdas
      if (pDeclaration.callee.type === 'Identifier') {
        pLambdas.push(pDeclaration.callee.name);
      }
      else if (pDeclaration.callee.type === 'Literal') {
        throw new MLExpressionValidationException('Unexpected token ILLEGAL');
      }
      else {
        processDeclaration(pDeclaration.callee, pParameters, pLambdas);
      }
    }
    else if (pDeclaration.type === 'BinaryExpression') {
      // Example: 100 + TOP + 'hi'
      // process left and right parts of the binary expression
      processDeclaration(pDeclaration.left, pParameters, pLambdas);
      processDeclaration(pDeclaration.right, pParameters, pLambdas);
    }
    else if (pDeclaration.type === 'MemberExpression') {
      // Example: TOP['hi'] or TOP.hi
      // process the object and the property;
      processDeclaration(pDeclaration.object, pParameters, pLambdas);
      if (pDeclaration.computed) {
        processDeclaration(pDeclaration.property, pParameters, pLambdas);
      }
    }
    else if (pDeclaration.type === 'UpdateExpression') {
      // Example: X++ or ++X;
      // Note (AJ) this is unsupported because at the moment X++ and ++X is an assignment and we (at the moment)
      // do not update variables in the same place they are used ( i.e. in instructions )
      throw new MLExpressionValidationException('Update expressions are not supported');
    }
    else if (pDeclaration.type === 'LogicalExpression') {
      // Example: X && Y or X && Y && Z
      // process only the left and right sides of the logical expression, ignore the operator
      processDeclaration(pDeclaration.left, pParameters, pLambdas);
      processDeclaration(pDeclaration.right, pParameters, pLambdas);
    }
    else if (pDeclaration.type === 'ConditionalExpression') {
      // Example: X ? Y : Z
      // process only the test (X), the consequent (Y) and the alternate (Z).
      processDeclaration(pDeclaration.test, pParameters, pLambdas);
      processDeclaration(pDeclaration.consequent, pParameters, pLambdas);
      processDeclaration(pDeclaration.alternate, pParameters, pLambdas);

    }
    else if (pDeclaration.type === 'Identifier') {
      // add to list of parameters
      pParameters.push(pDeclaration.name);
    }
    else if (pDeclaration.type === 'Literal') {
      // do nothing
    }
    else {
      // do nothing
    }
  }

  // Numbers are always valid expressions. This skips the "isExpressionValid" function
  return function (pExpression) {
    if (typeof pExpression !== 'string') { return 'Expressions must be a string'; }
    if (pExpression[pExpression.length - 1] === ';') { return 'Unexpected token ILLEGAL'; }
    if (pExpression.length == 0) { return; }

    let lAppParameters = [];
    let lAppLambdas = [];

    if (pApplicationParametersObject) {
      lAppParameters = Object.values(pApplicationParametersObject.mParameters).filter(pParameter => pParameter.type !== 'lambda').map(pParam => pParam.name);
      lAppLambdas = Object.values(pApplicationParametersObject.mParameters).filter(pParameter => pParameter.type === 'lambda').map(pParam => pParam.name);
    }

    const lParameters = [];
    const lLambdas = [];
    const lExpressionDefinitionString = `const exp = ${pExpression}`;
    try {
      const lEsprimaResult = esprima.parseScript(lExpressionDefinitionString);

      if (lEsprimaResult.body.length > 1) { return `Only one expression declaration allowed`; }

      processDeclaration(lEsprimaResult.body[0].declarations[0].init, lParameters, lLambdas);

      const lUndefinedVariable = lParameters.find(pParameter => !lAppParameters.includes(pParameter));
      if (lUndefinedVariable) { return `Application parameter is undefined: ${lUndefinedVariable}`; }

      const lUndefinedLambda = lLambdas.find(pLambda => !lAppLambdas.includes(pLambda));
      if (lUndefinedVariable) { return `Application lambda is undefined: ${lUndefinedLambda}`; }
    }
    catch (e) {
      return e.description;
    }

    return undefined; // Expression is valid
  };
}